import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Progress } from '@mantine/core';
import axios from 'axios';
import Video from 'twilio-video';

import Room from './Room';
import settings from '../../config/settings';
import StreamingOffBox from '../StreamingOffBox';
import useUserData from '../../store/useUserStore';
import logger from '../../utils/logger';
import useSessionId from '../../hooks/useSessionId';
import { replaceTwilioParticipantIdentity } from '../../utils/twilioParticipantIdentity';

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 2000;
const DUPLICATE_IDENTITY_ERROR_CODE = 53205;

const createConnectionKey = (roomId, identity, options) =>
  [
    roomId,
    identity,
    Boolean(options?.audio),
    Boolean(options?.video),
  ].join(':');

const VideoChat = () => {
  const sessionId = useSessionId();
  const roomOptions = useUserData(state => state.roomOptions);
  const participantSessionId = useUserData(
    state => state.participantSessionId
  );
  const userProfileId = useUserData(
    state => state.userProfile.userProfileId
  );
  const videoRoomId = useUserData(state => state.videoRooms.videoRoomId);
  const setParticipantSessionId = useUserData(
    state => state.setParticipantSessionId
  );
  const setTokenId = useUserData(state => state.setTokenId);

  const [room, setRoom] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const roomRef = useRef(null);
  const currentConnectionKeyRef = useRef(null);
  const desiredConnectionRef = useRef(null);
  const connectPromiseRef = useRef(null);
  const tokenRequestAbortRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const syncConnectionRef = useRef(() => Promise.resolve(null));
  const roomListenersRef = useRef(new WeakMap());
  const roomEventHandlersRef = useRef({});

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const detachRoomListeners = useCallback(targetRoom => {
    const listeners = roomListenersRef.current.get(targetRoom);
    if (!listeners) return;

    targetRoom.off('reconnecting', listeners.reconnecting);
    targetRoom.off('reconnected', listeners.reconnected);
    targetRoom.off('disconnected', listeners.disconnected);
    roomListenersRef.current.delete(targetRoom);
  }, []);

  const attachRoomListeners = useCallback(targetRoom => {
    if (roomListenersRef.current.has(targetRoom)) return;

    const listeners = {
      reconnecting: error =>
        roomEventHandlersRef.current.reconnecting?.(targetRoom, error),
      reconnected: () =>
        roomEventHandlersRef.current.reconnected?.(targetRoom),
      disconnected: (disconnectedRoom, error) =>
        roomEventHandlersRef.current.disconnected?.(disconnectedRoom, error),
    };

    targetRoom.on('reconnecting', listeners.reconnecting);
    targetRoom.on('reconnected', listeners.reconnected);
    targetRoom.on('disconnected', listeners.disconnected);
    roomListenersRef.current.set(targetRoom, listeners);
  }, []);

  const disconnectRoom = useCallback(
    targetRoom => {
      const activeRoom = targetRoom ?? roomRef.current;
      if (!activeRoom) return;

      const isCurrentRoom = roomRef.current === activeRoom;

      if (isCurrentRoom) {
        roomRef.current = null;
        currentConnectionKeyRef.current = null;
      }

      activeRoom.localParticipant?.tracks?.forEach(trackPublication => {
        try {
          trackPublication.track?.stop?.();
        } catch (error) {
          console.warn('Failed to stop local Twilio track', error);
        }
      });

      detachRoomListeners(activeRoom);

      if (activeRoom.state !== 'disconnected') {
        try {
          activeRoom.disconnect();
        } catch (error) {
          console.warn('Failed to disconnect Twilio room', error);
        }
      }

      if (isCurrentRoom) setTokenId(null);

      if (isMountedRef.current) {
        setRoom(currentRoom =>
          currentRoom === activeRoom ? null : currentRoom
        );
      }
    },
    [detachRoomListeners, setTokenId]
  );

  const scheduleReconnect = useCallback(
    (connectionKey, error) => {
      if (
        !connectionKey ||
        error?.code === DUPLICATE_IDENTITY_ERROR_CODE ||
        error?.message === 'Room completed' ||
        reconnectTimerRef.current ||
        reconnectCountRef.current >= MAX_RECONNECT_ATTEMPTS ||
        desiredConnectionRef.current?.key !== connectionKey
      ) {
        return;
      }

      reconnectCountRef.current += 1;
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        syncConnectionRef.current();
      }, RECONNECT_DELAY_MS);
    },
    []
  );

  const syncConnection = useCallback(async () => {
    const desiredConnection = desiredConnectionRef.current;

    if (!desiredConnection) {
      disconnectRoom();
      return null;
    }

    if (
      roomRef.current?.state === 'connected' &&
      currentConnectionKeyRef.current === desiredConnection.key
    ) {
      return roomRef.current;
    }

    if (connectPromiseRef.current) {
      const pendingConnection = connectPromiseRef.current;

      await pendingConnection.catch(() => null);

      if (connectPromiseRef.current === pendingConnection) {
        connectPromiseRef.current = null;
      }

      if (
        desiredConnectionRef.current &&
        !roomRef.current
      ) {
        return syncConnectionRef.current();
      }

      return roomRef.current;
    }

    disconnectRoom();
    clearReconnectTimer();

    const attemptKey = desiredConnection.key;
    const abortController = new AbortController();
    tokenRequestAbortRef.current = abortController;

    const connectionPromise = (async () => {
      if (isMountedRef.current) setIsLoading(true);

      const response = await axios.post(
        settings.generateAccessToken,
        {
          identity: desiredConnection.identity,
          roomName: desiredConnection.roomId,
        },
        { signal: abortController.signal }
      );

      if (desiredConnectionRef.current?.key !== attemptKey) return null;

      const connectedRoom = await Video.connect(response.data, {
        name: desiredConnection.roomId,
        ...desiredConnection.options,
      });

      if (
        !isMountedRef.current ||
        desiredConnectionRef.current?.key !== attemptKey
      ) {
        disconnectRoom(connectedRoom);
        return null;
      }

      roomRef.current = connectedRoom;
      currentConnectionKeyRef.current = attemptKey;
      reconnectCountRef.current = 0;
      attachRoomListeners(connectedRoom);
      setTokenId(response.data);
      setRoom(connectedRoom);

      return connectedRoom;
    })();

    connectPromiseRef.current = connectionPromise;

    try {
      return await connectionPromise;
    } catch (error) {
      const requestWasCancelled =
        axios.isCancel(error) || error?.code === 'ERR_CANCELED';

      if (!requestWasCancelled) {
        console.error("Can't connect to room: ", error);
        logger.log(new Error(`Cant connect to room: ${error}`));
        scheduleReconnect(attemptKey, error);
      }

      return null;
    } finally {
      if (connectPromiseRef.current === connectionPromise) {
        connectPromiseRef.current = null;
      }
      if (tokenRequestAbortRef.current === abortController) {
        tokenRequestAbortRef.current = null;
      }
      if (isMountedRef.current) setIsLoading(false);

      if (
        desiredConnectionRef.current &&
        !roomRef.current &&
        desiredConnectionRef.current.key !== attemptKey
      ) {
        Promise.resolve().then(() => syncConnectionRef.current());
      }
    }
  }, [
    attachRoomListeners,
    clearReconnectTimer,
    disconnectRoom,
    scheduleReconnect,
    setTokenId,
  ]);

  syncConnectionRef.current = syncConnection;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      desiredConnectionRef.current = null;
      clearReconnectTimer();
      tokenRequestAbortRef.current?.abort();
      disconnectRoom();
    };
  }, [clearReconnectTimer, disconnectRoom]);

  useEffect(() => {
    if (!videoRoomId || !participantSessionId) {
      desiredConnectionRef.current = null;
      syncConnectionRef.current();
      return undefined;
    }

    const connectionKey = createConnectionKey(
      videoRoomId,
      participantSessionId,
      roomOptions
    );

    desiredConnectionRef.current = {
      key: connectionKey,
      roomId: videoRoomId,
      identity: participantSessionId,
      options: roomOptions,
    };
    reconnectCountRef.current = 0;
    syncConnectionRef.current();

    return () => {
      if (desiredConnectionRef.current?.key === connectionKey) {
        desiredConnectionRef.current = null;
      }
      clearReconnectTimer();
      tokenRequestAbortRef.current?.abort();
      disconnectRoom();
    };
  }, [
    clearReconnectTimer,
    disconnectRoom,
    participantSessionId,
    roomOptions,
    videoRoomId,
  ]);

  roomEventHandlersRef.current = {
    reconnecting: (connectedRoom, error) => {
      logger.leaveBreadcrumb(`Twilio room reconnecting: ${error}`);
    },

    reconnected: connectedRoom => {
      reconnectCountRef.current = 0;
      logger.leaveBreadcrumb('Twilio room reconnected');
    },

    disconnected: (disconnectedRoom, error) => {
      detachRoomListeners(disconnectedRoom);
      const connectionKey = currentConnectionKeyRef.current;

      if (roomRef.current === disconnectedRoom) {
        roomRef.current = null;
        currentConnectionKeyRef.current = null;
        if (isMountedRef.current) setRoom(null);
        setTokenId(null);
      }

      if (!error) return;

      logger.log(
        new Error(
          `Twilio room disconnected: ${error.code ?? ''} ${error.message}`
        )
      );

      if (error.code === DUPLICATE_IDENTITY_ERROR_CODE) {
        const disconnectedConnection = desiredConnectionRef.current;
        const nextParticipantSessionId =
          replaceTwilioParticipantIdentity(sessionId);

        if (disconnectedConnection) {
          desiredConnectionRef.current = {
            ...disconnectedConnection,
            identity: nextParticipantSessionId,
            key: createConnectionKey(
              disconnectedConnection.roomId,
              nextParticipantSessionId,
              disconnectedConnection.options
            ),
          };
        }

        setParticipantSessionId(nextParticipantSessionId);
        Promise.resolve().then(() => {
          if (
            isMountedRef.current &&
            !roomRef.current &&
            desiredConnectionRef.current?.identity ===
              nextParticipantSessionId
          ) {
            syncConnectionRef.current();
          }
        });
        return;
      }

      scheduleReconnect(connectionKey, error);
    },
  };

  useEffect(() => {
    const handlePageExit = event => {
      if (event.persisted) return;

      desiredConnectionRef.current = null;
      clearReconnectTimer();
      disconnectRoom();
    };

    window.addEventListener('pagehide', handlePageExit);
    window.addEventListener('beforeunload', handlePageExit);

    return () => {
      window.removeEventListener('pagehide', handlePageExit);
      window.removeEventListener('beforeunload', handlePageExit);
    };
  }, [clearReconnectTimer, disconnectRoom]);

  if (isLoading && !room) {
    return (
      <Progress color="red" radius="xs" size="sm" value={100} striped animate />
    );
  }

  if (!room) return <StreamingOffBox />;

  return (
    <Room
      key={videoRoomId}
      userProfileId={userProfileId}
      room={room}
    />
  );
};

export default VideoChat;
